<template>
    <div>
        <v-alert v-if="!image" type="warning" variant="tonal" class="mb-3">
            {{ t('Hotspot_editor_sin_imagen') }}
        </v-alert>
        <template v-else>
            <p class="text-body-2 mb-2">
                {{ t('Hotspot_editor_instrucciones') }}
            </p>
            <div class="polygon-editor">
                <img :src="image" :alt="t('Hotspot_editor_imagen_alt')"
                    draggable="false" class="polygon-image" />
                <svg class="polygon-overlay" viewBox="0 0 1000 1000"
                    preserveAspectRatio="none" role="img"
                    :aria-label="t('Hotspot_editor_svg_label')"
                    @click="agregarPunto">
                    <polygon v-if="puntos.length >= 3" :points="listaPuntos"
                        fill="rgba(25,118,210,.2)" stroke="#1976d2" stroke-width="3"
                        vector-effect="non-scaling-stroke" />
                    <polyline v-else-if="puntos.length > 1" :points="listaPuntos"
                        fill="none" stroke="#1976d2" stroke-width="3"
                        vector-effect="non-scaling-stroke" />
                    <circle v-for="(punto, indice) in puntos" :key="indice"
                        :cx="punto[0]" :cy="punto[1]" r="7" fill="#fff"
                        stroke="#1976d2" stroke-width="3" vector-effect="non-scaling-stroke"
                        @click.stop />
                </svg>
            </div>
            <div class="d-flex align-center flex-wrap ga-2 mt-3">
                <v-btn size="small" variant="tonal" :disabled="!puntos.length"
                    @click="deshacer">{{ t('Hotspot_editor_deshacer') }}</v-btn>
                <v-btn size="small" variant="outlined" color="error"
                    :disabled="!puntos.length" @click="limpiar">{{ t('Hotspot_editor_redibujar') }}</v-btn>
                <span class="text-caption">{{ puntos.length }} {{ t('Hotspot_editor_vertices') }}</span>
            </div>
            <p v-if="puntos.length > 0 && puntos.length < 3"
                class="text-error text-caption mt-2">
                {{ t('Hotspot_editor_faltan_vertices', { cantidad: 3 - puntos.length }) }}
            </p>
        </template>
    </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
    modelValue: { type: [Object, Array], default: null },
    image: { type: String, default: null },
});
const emit = defineEmits(['update:modelValue']);
const { t } = useI18n();

function puntosIniciales(region) {
    const anillo = Array.isArray(region)
        ? region
        : region?.type === 'Polygon' ? region.coordinates?.[0] : null;
    if (!Array.isArray(anillo)) return [];
    const puntos = anillo.map(p => [Number(p[0]), Number(p[1])]);
    if (puntos.length > 1 &&
        puntos[0][0] === puntos[puntos.length - 1][0] &&
        puntos[0][1] === puntos[puntos.length - 1][1]) {
        puntos.pop();
    }
    return puntos;
}

const puntos = ref(puntosIniciales(props.modelValue));
const listaPuntos = computed(() => puntos.value.map(p => p.join(',')).join(' '));

function emitirRegion() {
    if (puntos.value.length < 3) {
        emit('update:modelValue', null);
        return;
    }
    const anillo = [...puntos.value.map(p => [...p]), [...puntos.value[0]]];
    emit('update:modelValue', { type: 'Polygon', coordinates: [anillo] });
}

function agregarPunto(event) {
    const rect = event.currentTarget.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const x = Math.round((event.clientX - rect.left) / rect.width * 1000);
    const y = Math.round((event.clientY - rect.top) / rect.height * 1000);
    puntos.value.push([
        Math.max(0, Math.min(1000, x)),
        Math.max(0, Math.min(1000, y)),
    ]);
    emitirRegion();
}

function deshacer() {
    puntos.value.pop();
    emitirRegion();
}

function limpiar() {
    puntos.value = [];
    emitirRegion();
}
</script>

<style scoped>
.polygon-editor {
    position: relative;
    width: 100%;
    max-width: 780px;
    line-height: 0;
}
.polygon-image {
    display: block;
    width: 100%;
    height: auto;
    user-select: none;
}
.polygon-overlay {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    cursor: crosshair;
    touch-action: manipulation;
}
</style>
